// @ts-strict-ignore
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelRowProps, SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { getCustomerAddress } from 'pages/Customers/helpers/utils'
import { getCountryNameByCode } from 'utils/utils'
import {
  EMPLOYMENT_INDUSTRY_SECTOR_OPTIONS,
  ESTIMATED_YEARLY_INCOME_OPTIONS,
} from '../helpers/constants'
import {
  INDIVIDUAL_DESCRIPTION,
  INDIVIDUAL_EMPLOYMENT_STATUS_LABELS,
  INDIVIDUAL_PEP_STATUS,
  INDIVIDUAL_SOURCE_OF_FUNDS,
} from '../helpers/form'
import { EmbeddedCustomerEntityDetailsProps, EmbeddedIndividualCustomer } from '../helpers/types'
import { getCddIndividual } from '../helpers/utils'

export const IndividualCustomer = ({
  customer: individual,
  sharedCustomerDetails,
}: EmbeddedCustomerEntityDetailsProps<EmbeddedIndividualCustomer>) => {
  const { t } = useTranslation()
  const { person, details, cdd } = individual || {}
  const { contactInfo, taxIdentification, birthCountryCode } = details || {}
  const { data: countries } = useCountriesQuery(true)

  const customerDetailsRows: SidePanelRowProps[] = [
    ...sharedCustomerDetails,
    { label: t('fullName'), content: `${person?.firstName} ${person?.lastName}` },
    { label: t('dateOfBirth'), content: person?.dateOfBirth },
    {
      label: t('customers.representative.nationality'),
      content: getCountryNameByCode(birthCountryCode ?? '', countries ?? []),
    },
    { label: t('email'), content: contactInfo?.emailAddress },
    { label: t('phoneNumber'), content: contactInfo?.phoneNumber },
    { label: t('address'), content: getCustomerAddress(person?.address, { useFullAddress: true }) },
  ]

  const cddDetailsRows: SidePanelRowProps[] = [
    {
      label: t('customers.taxIdentification'),
      content: taxIdentification
        ? `${taxIdentification?.number}, ${taxIdentification?.taxResidenceCountryCode}`
        : '',
    },
    {
      label: t('customers.employmentStatus'),
      content: cdd?.employmentStatus
        ? t(
            `embeddedCustomers.individualOptions.${getCddIndividual(cdd?.employmentStatus ?? '', INDIVIDUAL_EMPLOYMENT_STATUS_LABELS)}`
          )
        : '',
    },
    {
      label: t('customers.sourceOfFunds'),
      content: cdd?.sourceOfFunds
        ? t(
            `embeddedCustomers.individualOptions.${getCddIndividual(cdd?.sourceOfFunds ?? '', INDIVIDUAL_SOURCE_OF_FUNDS)}`
          )
        : '',
    },
    {
      label: t('customers.pepStatus'),
      content: cdd?.pepStatus
        ? t(
            `embeddedCustomers.individualOptions.${getCddIndividual(cdd?.pepStatus ?? '', INDIVIDUAL_PEP_STATUS)}`
          )
        : '',
    },
    {
      label: t('customers.intendedUse'),
      content: cdd?.intendedUseOfAccount
        ? t(
            `embeddedCustomers.individualOptions.${getCddIndividual(cdd?.intendedUseOfAccount ?? '', INDIVIDUAL_DESCRIPTION)}`
          )
        : '',
    },
    {
      label: t('customers.monthlyVolumes'),
      content: `${cdd?.expectedMonthlyVolume?.amount ?? ''} ${cdd?.expectedMonthlyVolume?.currency ?? ''}`,
    },
    {
      label: t('customers.estimatedYearlyIncome'),
      content: cdd?.estimatedYearlyIncome
        ? (ESTIMATED_YEARLY_INCOME_OPTIONS?.find(
            ({ value }) => value === cdd?.estimatedYearlyIncome
          )?.leftText as string)
        : '',
    },
    {
      label: t('customers.employmentIndustrySector'),
      content: cdd?.employmentIndustrySector
        ? (EMPLOYMENT_INDUSTRY_SECTOR_OPTIONS?.find(
            ({ value }) => value === cdd?.employmentIndustrySector
          )?.leftText as string)
        : '',
    },
  ]

  return (
    <>
      <SidePanelSection heading={t('embeddedCustomers.details.customerDetails')}>
        <SidePanelSummary rows={customerDetailsRows} />
      </SidePanelSection>
      {cdd && (
        <SidePanelSection heading={t('customers.complianceInformation')}>
          <SidePanelSummary rows={cddDetailsRows} />
        </SidePanelSection>
      )}
    </>
  )
}
